import React, { useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '@/shared/hooks/useWebSocket';
import { GlobalContext } from '@/app/providers/globalState';
import { debounce } from 'lodash';
import { notification, Button } from 'antd';
import { ready_to_deploy } from '@/shared/constants/taskStatuses';
import { sparkToast } from '@spark';
import { triggerModelDownload, notifyDownloadFailure } from '@/features/trainings/utils/modelDownload';
import { requestGatedModelDownload } from '@/features/billing/utils/modelDownloadRequest';
import { taskUrlForUsecase } from '@/features/tasks/utils/navigation';

/**
 * "Download model" from the training-complete toast: quote through the
 * global ``DownloadQuoteGate`` (``HorizontalNavBar``) and only then request
 * the zip. ``onPaymentRequired`` reopens the gate's "Not enough credits" modal
 * if the request still answers 402, so the FreeUsageExhausted modal is never
 * shown for a gated download.
 */
export function requestTrainingCompletedDownload(usecase, setProgressStatus) {
    requestGatedModelDownload({
        items: [{
            llm_usecase_id: usecase._id,
            version: usecase.version,
            label: `${usecase.usecase_name || usecase.name || 'Model'} v${usecase.version}`,
        }],
        onApproved: ({ onPaymentRequired } = {}) => triggerModelDownload({
            clientId: usecase.client_id,
            usecaseId: usecase._id,
            version: usecase.version,
            setProgressStatus,
            suppressFreeUsageGate: true,
            onError: (detail, cause) => {
                if (cause?.status === 402 && onPaymentRequired) {
                    onPaymentRequired();
                    return;
                }
                notifyDownloadFailure(detail);
            },
        }),
    });
}

const TRAINING_COMPLETED_MESSAGE = "Training completed successfully";

export const useUpdatesListener = () => {
    const {
        handleWsMessage,
        lastMessages,
        allUsecases,
        setProgressStatus,
        setCurrentUsecaseId,
        requestRealtimeResync,
    } = useContext(GlobalContext);

    const navigate = useNavigate();

    useWebSocket('/api/updates-ws', handleWsMessage, requestRealtimeResync);

    // A backgrounded/sleeping tab can hold a half-open socket that silently
    // missed updates without ever firing onclose, so also resync when the
    // user comes back to the tab.
    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                requestRealtimeResync();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, [requestRealtimeResync]);

    const showMessage = useCallback(debounce((type, content) => {
        sparkToast[type](content);
    }, 100), []);

    const showTrainingCompletedNotification = useCallback((usecaseId) => {
        const usecase = allUsecases.find(u => u._id === usecaseId);
        if (!usecase) return false;

        const notificationKey = `training-completed-${usecaseId}-${Date.now()}`;

        const handleDownload = () => {
            requestTrainingCompletedDownload(usecase, setProgressStatus);
            notification.destroy(notificationKey);
        };

        const handleViewModal = () => {
            setCurrentUsecaseId(usecaseId);
            const taskUrl = taskUrlForUsecase(usecase, {
                task_id: usecaseId,
                action: 'evaluation-from-session',
            });
            if (taskUrl) {
                navigate(taskUrl);
            }
            notification.destroy(notificationKey);
        };

        sparkToast.success('Training completed, model created successfully.', {
            content: (
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 8 }}>
                    <a
                        onClick={handleDownload}
                        style={{ textDecoration: 'underline', cursor: 'pointer', color: '#1677ff', fontSize: 14 }}
                    >
                        Download model
                    </a>
                    <Button type="primary" onClick={handleViewModal}>
                        View Model
                    </Button>
                </div>
            ),
        });

        return true;
    }, [allUsecases, setProgressStatus, setCurrentUsecaseId, navigate]);

    useEffect(() => {
        if (!lastMessages) {
            return;
        }

        const isTrainingCompleted =
            lastMessages.status === ready_to_deploy &&
            lastMessages.message === TRAINING_COMPLETED_MESSAGE &&
            !lastMessages.isError;

        if (isTrainingCompleted && lastMessages.usecaseId) {
            if (showTrainingCompletedNotification(lastMessages.usecaseId)) {
                return;
            }
        }

        showMessage(lastMessages.isError ? 'error' : 'success', lastMessages.message);
    }, [lastMessages]);
}
